import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { useOffline } from '../context/OfflineContext';
import { 
  Wrench, 
  Layers, 
  Zap, 
  Paintbrush, 
  Home, 
  HardHat,
  ArrowUp,
  Palette,
  Loader2,
  ArrowLeft,
  Package,
  WifiOff,
  FileText,
  BookTemplate,
  Trash2,
  Upload,
  Download
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping for dynamic trades
const iconMap = {
  Wrench: Wrench,
  Layers: Layers,
  Zap: Zap,
  Paintbrush: Paintbrush,
  Home: Home,
  HardHat: HardHat,
  ArrowUp: ArrowUp,
  Palette: Palette,
  Package: Package, // default
};

export default function Templates() {
  const navigate = useNavigate();
  const { isAdmin, isAdminOrManager } = useAuth();
  const { isOnline, cacheTrades, getCachedTrades, queueSync } = useOffline();
  const [trades, setTrades] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [formType, setFormType] = useState('');  // "1st Fix" or "2nd Fix"
  const [building, setBuilding] = useState('');
  const [floorNumber, setFloorNumber] = useState('');
  const [flatNumber, setFlatNumber] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [offlineMode, setOfflineMode] = useState(false);
  const [activeTab, setActiveTab] = useState('trades');
  
  // Delete and import states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [isOnline]);

  const fetchData = async () => {
    try {
      if (isOnline) {
        const [tradesRes, templatesRes] = await Promise.all([
          axios.get(`${API}/trades`),
          axios.get(`${API}/templates`)
        ]);
        setTrades(tradesRes.data);
        setTemplates(templatesRes.data);
        await cacheTrades(tradesRes.data);
        setOfflineMode(false);
      } else {
        const cachedTrades = await getCachedTrades();
        setTrades(cachedTrades);
        setOfflineMode(true);
      }
    } catch (error) {
      // Try cache on error
      try {
        const cachedTrades = await getCachedTrades();
        if (cachedTrades.length > 0) {
          setTrades(cachedTrades);
          setOfflineMode(true);
        } else {
          toast.error('Failed to load trades');
        }
      } catch (cacheError) {
        toast.error('Failed to load trades');
      }
    } finally {
      setLoading(false);
    }
  };

  // Delete template handler
  const handleDeleteClick = (e, template) => {
    e.stopPropagation(); // Prevent card click
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/templates/${templateToDelete.template_id}`);
      setTemplates(templates.filter(t => t.template_id !== templateToDelete.template_id));
      toast.success('Template deleted successfully');
    } catch (error) {
      toast.error('Failed to delete template');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  // Import template handler
  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.json')) {
      toast.error('Please select a JSON file');
      return;
    }

    setIsImporting(true);
    try {
      const text = await file.text();
      const templateData = JSON.parse(text);

      // Validate template structure
      if (!templateData.name || !templateData.trade_type) {
        toast.error('Invalid template: missing name or trade_type');
        return;
      }

      const response = await axios.post(`${API}/templates/import`, templateData);
      setTemplates([...templates, response.data]);
      toast.success(`Template "${templateData.name}" imported successfully`);
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON file');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to import template');
      }
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Export template handler
  const handleExportClick = async (e, template) => {
    e.stopPropagation(); // Prevent card click
    try {
      const response = await axios.get(`${API}/templates/${template.template_id}/export`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${template.name.replace(/[^a-z0-9]/gi, '_')}_template.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Template exported');
    } catch (error) {
      toast.error('Failed to export template');
    }
  };

  const handleTradeSelect = (trade) => {
    setSelectedTrade(trade);
    setSelectedTemplate(null);
    setFormType('');
    setBuilding('');
    setFloorNumber('');
    setFlatNumber('');
    setDialogOpen(true);
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setSelectedTrade(null);
    setFormType('');
    setBuilding('');
    setFloorNumber('');
    setFlatNumber('');
    setTemplateDialogOpen(true);
  };

  const handleCreateForm = async () => {
    if (!formType) {
      toast.error('Please select a form type');
      return;
    }

    setIsCreating(true);
    try {
      const tradeName = selectedTrade?.name || selectedTemplate?.trade_type;
      
      if (isOnline) {
        const formData = {
          trade_type: tradeName,
          form_type: formType,
          building: building.trim(),
          floor_number: floorNumber.trim(),
          flat_number: flatNumber.trim(),
          initial_pages: 3,
          from_template_id: selectedTemplate?.template_id || null
        };
        
        const response = await axios.post(`${API}/forms`, formData);
        
        toast.success('Form created successfully!');
        navigate(`/forms/${response.data.form_id}/edit`);
      } else {
        // Queue for sync when offline
        const formData = {
          trade_type: tradeName,
          form_type: formType,
          building: building.trim(),
          floor_number: floorNumber.trim(),
          flat_number: flatNumber.trim(),
          initial_pages: 3
        };
        await queueSync('CREATE_FORM', formData);
        toast.info('Form will be created when back online');
        setDialogOpen(false);
        setTemplateDialogOpen(false);
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error('Failed to create form');
    } finally {
      setIsCreating(false);
    }
  };

  const getIconComponent = (iconName) => {
    return iconMap[iconName] || Package;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  // Form creation dialog content (shared between trade and template dialogs)
  const renderFormCreationContent = () => (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label htmlFor="form-type">Form Type *</Label>
        <Select value={formType} onValueChange={setFormType} data-testid="form-type-select">
          <SelectTrigger id="form-type" data-testid="form-type-trigger">
            <SelectValue placeholder="Select form type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1st Fix" data-testid="form-type-1st-fix">1st Fix</SelectItem>
            <SelectItem value="2nd Fix" data-testid="form-type-2nd-fix">2nd Fix</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="building">Building</Label>
        <Input
          id="building"
          placeholder="e.g., Tower A"
          value={building}
          onChange={(e) => setBuilding(e.target.value)}
          data-testid="building-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="floor-number">Floor Number</Label>
          <Input
            id="floor-number"
            placeholder="e.g., 5"
            value={floorNumber}
            onChange={(e) => setFloorNumber(e.target.value)}
            data-testid="floor-number-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="flat-number">Flat / Unit Number</Label>
          <Input
            id="flat-number"
            placeholder="e.g., 501"
            value={flatNumber}
            onChange={(e) => setFlatNumber(e.target.value)}
            data-testid="flat-number-input"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="templates-page">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-4"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          <h1 className="font-heading text-3xl font-bold text-foreground">
            Create New QA Inspection Form
          </h1>
          <p className="text-muted-foreground mt-2">
            Choose a trade type or use an existing template
          </p>
          {offlineMode && (
            <div className="flex items-center gap-2 mt-2 text-amber-600">
              <WifiOff className="h-4 w-4" />
              <span className="text-sm">Offline mode - forms will sync when online</span>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="trades" className="flex items-center gap-2" data-testid="trades-tab">
              <FileText className="h-4 w-4" />
              By Trade Type
            </TabsTrigger>
            <TabsTrigger value="templates" className="flex items-center gap-2" data-testid="templates-tab">
              <BookTemplate className="h-4 w-4" />
              From Template
              {templates.length > 0 && (
                <Badge variant="secondary" className="ml-1">{templates.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="trades">
            {trades.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading text-lg font-semibold mb-2">No trades available</h3>
                  <p className="text-muted-foreground">
                    Contact an admin to add trade templates
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {trades.map(trade => {
                  const Icon = getIconComponent(trade.icon);
                  return (
                    <Card 
                      key={trade.trade_id}
                      className="trade-card overflow-hidden cursor-pointer hover:border-primary/50 hover:shadow-lg transition-all"
                      onClick={() => handleTradeSelect(trade)}
                      data-testid={`trade-card-${trade.trade_id}`}
                    >
                      <div className="relative h-40 overflow-hidden bg-slate-200">
                        {trade.image_url ? (
                          <img 
                            src={trade.image_url} 
                            alt={trade.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Icon className="h-16 w-16 text-slate-400" />
                          </div>
                        )}
                        <div className={`absolute top-3 right-3 w-10 h-10 ${trade.color} rounded-lg flex items-center justify-center`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-heading text-lg font-semibold mb-1">{trade.name}</h3>
                        <p className="text-sm text-muted-foreground">{trade.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="templates">
            {/* Import button for admin/manager */}
            {isAdminOrManager && isAdminOrManager() && (
              <div className="mb-6 flex justify-end">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".json"
                  className="hidden"
                />
                <Button
                  onClick={handleImportClick}
                  disabled={isImporting}
                  variant="outline"
                  className="gap-2"
                  data-testid="import-template-btn"
                >
                  {isImporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  Import Template
                </Button>
              </div>
            )}
            
            {templates.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <BookTemplate className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-heading text-lg font-semibold mb-2">No templates available</h3>
                  <p className="text-muted-foreground mb-4">
                    {isAdminOrManager && isAdminOrManager()
                      ? 'Create templates by saving forms as templates or import from a file'
                      : 'Contact an admin to create templates for your site'
                    }
                  </p>
                  {isAdminOrManager && isAdminOrManager() && (
                    <Button
                      onClick={handleImportClick}
                      disabled={isImporting}
                      variant="outline"
                      className="gap-2"
                    >
                      <Upload className="h-4 w-4" />
                      Import Template
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map(template => (
                  <Card 
                    key={template.template_id}
                    className="cursor-pointer hover:shadow-lg hover:border-primary/50 transition-all group"
                    onClick={() => handleTemplateSelect(template)}
                    data-testid={`template-card-${template.template_id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="font-heading text-lg">{template.name}</CardTitle>
                        <Badge variant="outline">{template.trade_type}</Badge>
                      </div>
                      {template.description && (
                        <CardDescription>{template.description}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm text-muted-foreground">
                        <p>Site: {template.site_name || 'All sites'}</p>
                        <p className="mt-1">Created: {new Date(template.created_at).toLocaleDateString()}</p>
                      </div>
                      
                      {/* Admin/Manager actions */}
                      {isAdminOrManager && isAdminOrManager() && (
                        <div className="flex gap-2 mt-4 pt-3 border-t opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 gap-1"
                            onClick={(e) => handleExportClick(e, template)}
                            data-testid={`export-template-${template.template_id}`}
                          >
                            <Download className="h-3 w-3" />
                            Export
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => handleDeleteClick(e, template)}
                            data-testid={`delete-template-${template.template_id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Form from Trade Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Create {selectedTrade?.name} Form
            </DialogTitle>
            <DialogDescription>
              Select the form type and enter location details
            </DialogDescription>
          </DialogHeader>
          {renderFormCreationContent()}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDialogOpen(false)}
              data-testid="cancel-create-btn"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateForm}
              disabled={isCreating || !formType}
              className="bg-primary hover:bg-primary/90"
              data-testid="create-form-btn"
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Form'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Form from Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              Create Form from Template
            </DialogTitle>
            <DialogDescription>
              Using template: {selectedTemplate?.name} ({selectedTemplate?.trade_type})
            </DialogDescription>
          </DialogHeader>
          {renderFormCreationContent()}
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setTemplateDialogOpen(false)}
              data-testid="cancel-template-create-btn"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateForm}
              disabled={isCreating || !formType}
              className="bg-primary hover:bg-primary/90"
              data-testid="create-template-form-btn"
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Form'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
